/**
 * Settings page — AI provider configuration and connection test.
 *
 * Layout:
 *   1. Active config summary (always visible)
 *   2. Backend health check
 *   3. Provider selector + Model selector + API key + Base URL
 *   4. Connection test with model correction display
 *   5. Language selector
 *   6. About
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Input,
  Text,
  Select,
  Field,
  MessageBar,
  MessageBarBody,
  Badge,
  Switch,
} from '@fluentui/react-components';
import {
  PlugConnectedRegular,
  EyeRegular,
  EyeOffRegular,
  ArrowResetRegular,
} from '@fluentui/react-icons';
import type { AIProvider, ResponseLanguage, FormulaLocale } from '@excel-ai-studio/shared';
import {
  DEFAULT_MODELS,
  PROVIDER_LABELS,
  PROVIDER_REQUIRES_KEY,
  PROVIDER_API_KEY_HINTS,
  PROVIDER_DEFAULT_BASE_URLS,
} from '@excel-ai-studio/shared';
import { getSettings, updateSettings, resetSettings, isSettingsReady } from '../store/settings.store';
import { testProvider, checkHealth } from '../services/api.client';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

// ─── Provider list ───────────────────────────────────────

const PROVIDERS = (Object.keys(PROVIDER_LABELS) as AIProvider[]).map((p) => ({
  value: p,
  label: PROVIDER_LABELS[p],
}));

const LANGUAGES: { value: ResponseLanguage; label: string }[] = [
  { value: 'fr', label: 'Francais' },
  { value: 'en', label: 'English' },
];

const FORMULA_LOCALES: { value: FormulaLocale; label: string; hint: string }[] = [
  { value: 'auto', label: 'Automatique', hint: 'Detecte depuis la langue d\'Excel (francais par defaut)' },
  { value: 'fr', label: 'Francais', hint: 'SOMME, MOYENNE, SI, RECHERCHEV, separateur ;' },
  { value: 'en', label: 'English', hint: 'SUM, AVERAGE, IF, VLOOKUP, separator ,' },
];

// ─── Component ───────────────────────────────────────────

export const SettingsPage: React.FC = () => {
  const [settings, setSettingsState] = useState(getSettings());
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    latencyMs?: number;
    resolvedModel?: string;
    modelCorrected?: boolean;
    originalModel?: string;
  }>();
  const [backendStatus, setBackendStatus] = useState<boolean>();
  const [showApiKey, setShowApiKey] = useState(false);
  const [learningLoading, setLearningLoading] = useState(false);
  const [learningStatus, setLearningStatus] = useState<{
    enabled: boolean;
    aiEnabled: boolean;
    mode: 'strict' | 'supervised' | 'off';
    pendingCandidates: number;
    totalCandidates: number;
    recentCandidates: Array<{
      id: string;
      createdAt: string;
      source: string;
      command: string;
      status: 'pending' | 'approved' | 'rejected' | 'applied';
      note?: string;
    }>;
  } | null>(null);
  const [learningError, setLearningError] = useState<string | null>(null);
  const [ruleAnalytics, setRuleAnalytics] = useState<{
    totalEvents: number;
    runtimeRuleEvents: number;
    byRule: Array<{ ruleId: string; hits: number; lastSeenAt?: string }>;
  } | null>(null);
  const [ruleConflicts, setRuleConflicts] = useState<{
    totalRules: number;
    conflicts: Array<{ ruleA: string; ruleB: string; overlapScore: number; reason: string; overlappingTerms: string[] }>;
  } | null>(null);

  useEffect(() => {
    checkHealth().then(setBackendStatus);
    void loadLearningStatus();
    void loadRuleAnalytics();
    void loadRuleConflicts();
  }, []);

  // ─── Handlers ────────────────────────────────────────

  const handleProviderChange = useCallback((provider: AIProvider) => {
    const models = DEFAULT_MODELS[provider];
    const baseUrl = PROVIDER_DEFAULT_BASE_URLS[provider];
    const updated = updateSettings({
      provider,
      model: models[0],
      apiKey: '',
      baseUrl,
    });
    setSettingsState(updated);
    setTestResult(undefined);
    setShowApiKey(false);
  }, []);

  const handleModelChange = useCallback((model: string) => {
    setSettingsState(updateSettings({ model }));
    setTestResult(undefined);
  }, []);

  const handleApiKeyChange = useCallback((apiKey: string) => {
    setSettingsState(updateSettings({ apiKey }));
    setTestResult(undefined);
  }, []);

  const handleBaseUrlChange = useCallback((baseUrl: string) => {
    setSettingsState(updateSettings({ baseUrl: baseUrl || undefined }));
    setTestResult(undefined);
  }, []);

  const handleLanguageChange = useCallback((language: ResponseLanguage) => {
    setSettingsState(updateSettings({ language }));
  }, []);

  const handleFormulaLocaleChange = useCallback((formulaLocale: FormulaLocale) => {
    setSettingsState(updateSettings({ formulaLocale }));
  }, []);

  const handleCopilotDebugModeChange = useCallback((enabled: boolean) => {
    setSettingsState(updateSettings({ copilotDebugMode: enabled }));
  }, []);

  const handleReset = useCallback(() => {
    setSettingsState(resetSettings());
    setTestResult(undefined);
    setShowApiKey(false);
  }, []);

  const loadLearningStatus = useCallback(async () => {
    setLearningLoading(true);
    setLearningError(null);
    try {
      const response = await fetch('/api/copilot/learning/status');
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      setLearningStatus(json);
    } catch (error) {
      setLearningError(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setLearningLoading(false);
    }
  }, []);

  const loadRuleAnalytics = useCallback(async () => {
    try {
      const response = await fetch('/api/copilot/learning/runtime-rules/analytics');
      if (!response.ok) return;
      const json = await response.json();
      setRuleAnalytics(json);
    } catch {
      // keep quiet in settings page
    }
  }, []);

  const loadRuleConflicts = useCallback(async () => {
    try {
      const response = await fetch('/api/copilot/learning/runtime-rules/conflicts');
      if (!response.ok) return;
      const json = await response.json();
      setRuleConflicts(json);
    } catch {
      // keep quiet in settings page
    }
  }, []);

  const reviewCandidate = useCallback(
    async (candidateId: string, decision: 'approve' | 'reject') => {
      try {
        const response = await fetch('/api/copilot/learning/review', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            candidateId,
            decision,
            reviewer: 'settings-ui',
            applyNow: true,
          }),
        });
        if (!response.ok) {
          throw new Error(`API error: ${response.statusText}`);
        }
        await loadLearningStatus();
      } catch (error) {
        setLearningError(error instanceof Error ? error.message : 'Erreur inconnue');
      }
    },
    [loadLearningStatus],
  );

  const handleTest = async () => {
    setTesting(true);
    setTestResult(undefined);

    try {
      const result = await testProvider({
        provider: settings.provider,
        apiKey: settings.apiKey || undefined,
        baseUrl: settings.baseUrl,
        model: settings.model,
      });
      setTestResult({
        success: result.success,
        message: result.success
          ? `Connexion reussie (${result.latencyMs}ms)`
          : result.message,
        latencyMs: result.latencyMs,
        resolvedModel: result.resolvedModel,
        modelCorrected: result.modelCorrected,
        originalModel: result.originalModel,
      });
    } catch (err) {
      setTestResult({ success: false, message: (err as Error).message });
    } finally {
      setTesting(false);
    }
  };

  // ─── Derived state ───────────────────────────────────

  const models = DEFAULT_MODELS[settings.provider] ?? [];
  const requiresKey = PROVIDER_REQUIRES_KEY[settings.provider];
  const apiKeyHint = PROVIDER_API_KEY_HINTS[settings.provider];
  const ready = isSettingsReady();
  const isOllama = settings.provider === 'ollama';

  // ─── Render ──────────────────────────────────────────

  return (
    <div>
      {/* ══ Active config summary ══ */}
      <div className="card">
        <Text className="section-title">Configuration active</Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Badge appearance="filled" color="brand" size="small">
            {PROVIDER_LABELS[settings.provider]}
          </Badge>
          <Badge appearance="outline" size="small">
            {settings.model}
          </Badge>
          {requiresKey && (
            <Badge
              appearance="filled"
              color={settings.apiKey ? 'success' : 'danger'}
              size="small"
            >
              {settings.apiKey ? 'Cle API configuree' : 'Cle API manquante'}
            </Badge>
          )}
          {!requiresKey && (
            <Badge appearance="filled" color="success" size="small">
              Pas de cle requise
            </Badge>
          )}
        </div>
        {!ready && (
          <MessageBar intent="warning" style={{ borderRadius: 4, marginTop: 8 }}>
            <MessageBarBody>
              Configurez votre cle API pour utiliser les fonctionnalites IA.
            </MessageBarBody>
          </MessageBar>
        )}
      </div>

      {/* ══ Backend health ══ */}
      <div className="card">
        <Text className="section-title">Statut du backend</Text>
        {backendStatus === undefined ? (
          <LoadingSpinner label="Verification..." />
        ) : (
          <MessageBar intent={backendStatus ? 'success' : 'error'}>
            <MessageBarBody>
              {backendStatus
                ? 'Backend connecte et operationnel'
                : 'Backend non disponible. Demarrez-le avec : npm run dev:backend'}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>

      {/* ══ Provider configuration ══ */}
      <div className="card">
        <Text className="section-title">Fournisseur IA</Text>

        <Field label="Fournisseur" style={{ marginBottom: 12 }}>
          <Select
            value={settings.provider}
            onChange={(_e, data) => handleProviderChange(data.value as AIProvider)}
          >
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </Select>
        </Field>

        <Field label="Modele" style={{ marginBottom: 12 }}>
          <Select
            value={settings.model}
            onChange={(_e, data) => handleModelChange(data.value)}
          >
            {models.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </Select>
        </Field>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginTop: -8, marginBottom: 12 }}>
          Le modele selectionne sera utilise pour toutes les requetes IA (formules, chat).
          {isOllama && ' Verifiez que le modele est installe localement (ollama pull <modele>).'}
        </Text>

        {/* API Key */}
        {requiresKey && (
          <Field
            label="Cle API"
            hint={apiKeyHint || undefined}
            style={{ marginBottom: 12 }}
          >
            <div style={{ display: 'flex', gap: 4 }}>
              <Input
                type={showApiKey ? 'text' : 'password'}
                value={settings.apiKey}
                onChange={(_e, data) => handleApiKeyChange(data.value)}
                placeholder={`Cle API ${PROVIDER_LABELS[settings.provider]}`}
                style={{ flex: 1 }}
              />
              <Button
                icon={showApiKey ? <EyeOffRegular /> : <EyeRegular />}
                appearance="subtle"
                size="small"
                onClick={() => setShowApiKey((v) => !v)}
                title={showApiKey ? 'Masquer' : 'Afficher'}
              />
            </div>
          </Field>
        )}

        {/* Base URL (Ollama) */}
        {isOllama && (
          <Field
            label="URL du serveur Ollama"
            hint="Laissez vide pour utiliser http://localhost:11434"
            style={{ marginBottom: 12 }}
          >
            <Input
              value={settings.baseUrl ?? ''}
              onChange={(_e, data) => handleBaseUrlChange(data.value)}
              placeholder="http://localhost:11434"
              style={{ width: '100%' }}
            />
          </Field>
        )}

        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          {requiresKey
            ? 'La cle API est stockee uniquement dans le navigateur local. Elle n\'est jamais envoyee a nos serveurs.'
            : 'Ollama s\'execute localement. Aucune cle API necessaire.'}
        </Text>

        {/* Test connection */}
        <Button
          icon={<PlugConnectedRegular />}
          onClick={handleTest}
          disabled={testing || (requiresKey && !settings.apiKey)}
          size="small"
          appearance="primary"
        >
          Tester la connexion
        </Button>

        {testing && <LoadingSpinner label="Test en cours..." />}

        {testResult && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <MessageBar
              intent={testResult.success ? 'success' : 'error'}
              style={{ borderRadius: 4 }}
            >
              <MessageBarBody>{testResult.message}</MessageBarBody>
            </MessageBar>

            {/* Model correction warning */}
            {testResult.modelCorrected && (
              <MessageBar intent="warning" style={{ borderRadius: 4 }}>
                <MessageBarBody>
                  Le modele demande ({testResult.originalModel}) n&apos;est pas compatible avec{' '}
                  {PROVIDER_LABELS[settings.provider]}. Le modele {testResult.resolvedModel} a ete
                  utilise automatiquement. Selectionnez un modele compatible pour eviter cette correction.
                </MessageBarBody>
              </MessageBar>
            )}

            {/* Show resolved model on success */}
            {testResult.success && !testResult.modelCorrected && testResult.resolvedModel && (
              <Text size={200} style={{ color: 'var(--text-secondary)' }}>
                Modele utilise : {testResult.resolvedModel}
                {testResult.latencyMs != null && ` — Latence : ${testResult.latencyMs}ms`}
              </Text>
            )}
          </div>
        )}
      </div>

      {/* ══ Language ══ */}
      <div className="card">
        <Text className="section-title">Langue et locale des formules</Text>
        <Field label="Langue des reponses" style={{ marginBottom: 12 }}>
          <Select
            value={settings.language}
            onChange={(_e, data) => handleLanguageChange(data.value as ResponseLanguage)}
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </Select>
        </Field>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
          Langue utilisee pour les explications, les formules generees et les reponses du chat.
        </Text>

        <Field label="Locale des formules Excel" style={{ marginBottom: 8 }}>
          <Select
            value={settings.formulaLocale}
            onChange={(_e, data) => handleFormulaLocaleChange(data.value as FormulaLocale)}
          >
            {FORMULA_LOCALES.map((fl) => (
              <option key={fl.value} value={fl.value}>{fl.label}</option>
            ))}
          </Select>
        </Field>
        <Text size={200} style={{ color: 'var(--text-secondary)' }}>
          {FORMULA_LOCALES.find((fl) => fl.value === settings.formulaLocale)?.hint}
        </Text>
      </div>

      {/* ══ Copilot debug ══ */}
      <div className="card">
        <Text className="section-title">Copilot Debug</Text>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <Text size={300}>Mode debug Copilot</Text>
          <Switch
            checked={settings.copilotDebugMode}
            onChange={(_e, data) => handleCopilotDebugModeChange(data.checked)}
            label={settings.copilotDebugMode ? 'ON' : 'OFF'}
          />
        </div>
        <Text size={200} style={{ color: 'var(--text-secondary)' }}>
          Quand active, le panneau Copilot affiche le plan interne (intent, confidence, policy, slots),
          le provider utilise et le temps/parcours d'execution.
        </Text>
      </div>

      {/* ══ Learning Governor ══ */}
      <div className="card">
        <Text className="section-title">Surveillance Learning</Text>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <Button size="small" appearance="secondary" onClick={() => void loadLearningStatus()} disabled={learningLoading}>
            Rafraichir
          </Button>
          <Button size="small" appearance="secondary" onClick={() => void loadRuleAnalytics()} disabled={learningLoading}>
            Analytics
          </Button>
          <Button size="small" appearance="secondary" onClick={() => void loadRuleConflicts()} disabled={learningLoading}>
            Conflits
          </Button>
          {learningStatus && (
            <>
              <Badge appearance="filled" color={learningStatus.enabled ? 'success' : 'danger'} size="small">
                Learning {learningStatus.enabled ? 'ON' : 'OFF'}
              </Badge>
              <Badge appearance="outline" size="small">
                Mode: {learningStatus.mode}
              </Badge>
              <Badge appearance="outline" size="small">
                Pending: {learningStatus.pendingCandidates}
              </Badge>
              <Badge appearance="outline" size="small">
                Total: {learningStatus.totalCandidates}
              </Badge>
            </>
          )}
        </div>
        {learningLoading && <LoadingSpinner label="Chargement learning..." />}
        {learningError && (
          <MessageBar intent="error" style={{ borderRadius: 4, marginBottom: 8 }}>
            <MessageBarBody>{learningError}</MessageBarBody>
          </MessageBar>
        )}
        {learningStatus && (
          <>
            <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
              En mode <strong>supervised</strong>, vous pouvez corriger un bug via feedback UI, et laisser l&apos;IA proposer/appliquer des corrections de lexique.
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {learningStatus.recentCandidates.filter((c) => c.status === 'pending').slice(0, 8).map((candidate) => (
                <div key={candidate.id} style={{ border: '1px solid #ddd', borderRadius: 6, padding: 8 }}>
                  <Text size={200} style={{ display: 'block', marginBottom: 4 }}>
                    <strong>{candidate.source}</strong> — {new Date(candidate.createdAt).toLocaleString()}
                  </Text>
                  <Text size={200} style={{ display: 'block', marginBottom: 6 }}>
                    {candidate.command}
                  </Text>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="small" appearance="primary" onClick={() => void reviewCandidate(candidate.id, 'approve')}>
                      Approuver
                    </Button>
                    <Button size="small" appearance="secondary" onClick={() => void reviewCandidate(candidate.id, 'reject')}>
                      Rejeter
                    </Button>
                  </div>
                </div>
              ))}
              {learningStatus.pendingCandidates === 0 && (
                <Text size={200} style={{ color: 'var(--text-secondary)' }}>
                  Aucun candidat en attente.
                </Text>
              )}
            </div>
            {ruleAnalytics && (
              <div style={{ marginTop: 12 }}>
                <Text size={300} style={{ display: 'block', marginBottom: 4 }}>
                  Analytics regles
                </Text>
                <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Events: {ruleAnalytics.totalEvents} | Runtime-rule events: {ruleAnalytics.runtimeRuleEvents}
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {ruleAnalytics.byRule.slice(0, 8).map((item) => (
                    <Text key={item.ruleId} size={200}>
                      {item.ruleId} — hits: {item.hits}
                    </Text>
                  ))}
                </div>
              </div>
            )}
            {ruleConflicts && (
              <div style={{ marginTop: 12 }}>
                <Text size={300} style={{ display: 'block', marginBottom: 4 }}>
                  Conflits potentiels
                </Text>
                {ruleConflicts.conflicts.length === 0 ? (
                  <Text size={200} style={{ color: 'var(--text-secondary)' }}>
                    Aucun conflit detecte.
                  </Text>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {ruleConflicts.conflicts.slice(0, 8).map((c, idx) => (
                      <Text key={`${c.ruleA}-${c.ruleB}-${idx}`} size={200}>
                        {c.ruleA} ↔ {c.ruleB} (score {c.overlapScore}) — {c.overlappingTerms.join(', ')}
                      </Text>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ══ About + Reset ══ */}
      <div className="card">
        <Text className="section-title">A propos</Text>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          Excel AI Studio v0.3.1
        </Text>
        <Button
          icon={<ArrowResetRegular />}
          appearance="subtle"
          size="small"
          onClick={handleReset}
        >
          Reinitialiser tous les parametres
        </Button>
      </div>
    </div>
  );
};
